import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Spinner, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, Table } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

const CandleAnalyzeModal = ({ isOpen, toggle, onError }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [lastDeleted, setLastDeleted] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getCandleStats();
      setRows(data);
    } catch (exc) {
      console.error('Failed to load candle stats:', exc);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, load]);

  const handleDelete = async (row) => {
    const key = `${row.TickerId}-${row.Interval}`;
    if (!window.confirm(`Delete all ${row.Interval} candles for ${row.TickerCode} (${row.Count.toLocaleString()} candles)?`)) {
      return;
    }
    try {
      setDeletingKey(key);
      const result = await apiClient.deleteCandles({
        Interval: row.Interval,
        TickersFilter: { Id: row.TickerId },
      });
      setLastDeleted(`Deleted ${result.DeletedCount} ${row.Interval} candles for ${row.TickerCode}.`);
      setRows(prev => prev.filter(r => !(r.TickerId === row.TickerId && r.Interval === row.Interval)));
    } catch (exc) {
      console.error('Failed to delete candles:', exc);
      onError(exc.message || 'Failed to delete candles');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Candle Counts by Ticker &amp; Interval</ModalHeader>
      <ModalBody>
        {lastDeleted && (
          <Alert color="success" fade={false} toggle={() => setLastDeleted(null)}>{lastDeleted}</Alert>
        )}
        {loading ? (
          <div className="text-center py-4"><Spinner /></div>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Ticker</th>
                <th>Interval</th>
                <th className="text-end">Count</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const key = `${row.TickerId}-${row.Interval}`;
                const isDeleting = deletingKey === key;
                return (
                  <tr key={key}>
                    <td>{idx + 1}</td>
                    <td>{row.TickerCode} <span className="text-muted">[{row.TickerSource}]</span></td>
                    <td>{row.Interval}</td>
                    <td className="text-end">{row.Count.toLocaleString()}</td>
                    <td className="text-center">
                      <Button
                        color="white"
                        size="sm"
                        disabled={isDeleting || deletingKey !== null}
                        onClick={() => handleDelete(row)}
                      >
                        {isDeleting ? <Spinner size="sm" /> : '❌'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted">No candles found</td></tr>
              )}
            </tbody>
          </Table>
        )}
      </ModalBody>
    </Modal>
  );
};

export const AdminDataCleanup = () => {
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [tickerCleanupLoading, setTickerCleanupLoading] = useState(false);
  const [deleteBeforeLoading, setDeleteBeforeLoading] = useState(false);
  const [deleteBeforeTickerId, setDeleteBeforeTickerId] = useState('');
  const [deleteBeforeDate, setDeleteBeforeDate] = useState('');
  const [deleteBeforeInterval, setDeleteBeforeInterval] = useState('Day');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [interval, setInterval] = useState('Day');
  const [tickerTag, setTickerTag] = useState('');
  const [tickerId, setTickerId] = useState('');
  const [collectCandles, setCollectCandles] = useState(null);
  const [tickerTypes, setTickerTypes] = useState([]);
  const [selectedTickerTypes, setSelectedTickerTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');

  // Ticker cleanup state
  const [tickerCleanupSource, setTickerCleanupSource] = useState('');
  const [tickerCleanupId, setTickerCleanupId] = useState('');
  const [tickerCleanupTypes, setTickerCleanupTypes] = useState([]);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);

  React.useEffect(() => {
    apiClient.getTickerTypes()
      .then(types => setTickerTypes(types))
      .catch(err => console.error('Failed to fetch ticker types:', err));

    apiClient.getSources()
      .then(srcs => setSources(srcs))
      .catch(err => console.error('Failed to fetch sources:', err));
  }, []);

  const handleCleanup = async () => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete all ${interval} candles for the filtered tickers.\n\nThis action CANNOT be undone!\n\nDo you want to proceed?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const tickersFilter = {};

      if (tickerId.trim()) {
        const id = parseInt(tickerId.trim(), 10);
        if (isNaN(id)) {
          setError('Invalid Ticker ID. Please enter a valid number.');
          setLoading(false);
          return;
        }
        tickersFilter.Id = id;
      }

      if (collectCandles !== null) {
        tickersFilter.CollectCandles = collectCandles;
      }

      if (tickerTag.trim()) {
        tickersFilter.Tag = tickerTag.trim();
      }

      if (selectedTickerTypes.length > 0) {
        tickersFilter.Types = selectedTickerTypes;
      }

      if (selectedSource) {
        tickersFilter.Source = selectedSource;
      }

      const result = await apiClient.deleteCandles({
        Interval: interval,
        TickersFilter: tickersFilter
      });

      setSuccess(`Successfully deleted ${result.DeletedCount} candles for ${result.TickersCount} tickers.`);
    } catch (exc) {
      console.error('Failed to delete candles:', exc);
      setError(exc.message || 'Failed to delete candles');
    } finally {
      setLoading(false);
    }
  };

  const handleResetStaticDetails = async () => {
    const confirmMessage = `⚠️ WARNING: This will reset the StaticDetailsChecked field to false for ALL tickers in the database.\n\nThis will cause the system to re-fetch static details for ALL tickers on the next pull.\n\nDo you want to proceed?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setResetLoading(true);
      setError(null);
      setSuccess(null);

      const result = await apiClient.resetStaticDetails();

      setSuccess(`Successfully reset StaticDetailsChecked for ${result.UpdatedCount} tickers.`);
    } catch (exc) {
      console.error('Failed to reset static details:', exc);
      setError(exc.message || 'Failed to reset static details');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteBefore = async () => {
    if (!deleteBeforeTickerId.trim()) {
      setError('Ticker ID is required.');
      return;
    }
    const id = parseInt(deleteBeforeTickerId.trim(), 10);
    if (isNaN(id)) {
      setError('Invalid Ticker ID. Please enter a valid number.');
      return;
    }
    if (!deleteBeforeDate) {
      setError('Please select a date.');
      return;
    }
    const before = new Date(deleteBeforeDate);
    const confirmMessage = `⚠️ WARNING: This will permanently delete all ${deleteBeforeInterval} candles for ticker ID ${id} that are earlier than ${before.toLocaleDateString()}. This cannot be undone!\n\nDo you want to proceed?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      setDeleteBeforeLoading(true);
      setError(null);
      setSuccess(null);
      const result = await apiClient.deleteCandlesBefore({
        TickerId: id,
        Interval: deleteBeforeInterval,
        Before: before.toISOString(),
      });
      setSuccess(`Successfully deleted ${result.DeletedCount} candle(s) before ${before.toLocaleDateString()} for ticker ID ${id}.`);
    } catch (exc) {
      console.error('Failed to delete candles:', exc);
      setError(exc.message || 'Failed to delete candles');
    } finally {
      setDeleteBeforeLoading(false);
    }
  };

  const handleTickerCleanup = async () => {
    const filterParts = [];
    if (tickerCleanupSource) {
      filterParts.push(`Source: ${tickerCleanupSource}`);
    }
    if (tickerCleanupId) {
      filterParts.push(`ID: ${tickerCleanupId}`);
    }
    if (tickerCleanupTypes.length > 0) {
      filterParts.push(`Types: ${tickerCleanupTypes.join(', ')}`);
    }

    const filterDesc = filterParts.length > 0 ? ` (${filterParts.join(', ')})` : '';
    const confirmMessage = `⚠️ WARNING: This will permanently delete all tickers${filterDesc}.\n\nThis action CANNOT be undone!\n\nAll candles and related data for these tickers will also be deleted.\n\nDo you want to proceed?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setTickerCleanupLoading(true);
      setError(null);
      setSuccess(null);

      const tickersFilter = {};

      if (tickerCleanupSource) {
        tickersFilter.Source = tickerCleanupSource;
      }

      if (tickerCleanupId) {
        const id = parseInt(tickerCleanupId, 10);
        if (isNaN(id)) {
          setError('Invalid Ticker ID. Please enter a valid number.');
          setTickerCleanupLoading(false);
          return;
        }
        tickersFilter.Id = id;
      }

      if (tickerCleanupTypes.length > 0) {
        tickersFilter.Types = tickerCleanupTypes;
      }

      const result = await apiClient.deleteTickers({
        TickersFilter: tickersFilter
      });

      if (result.CandlesDeleted > 0) {
        setSuccess(`Successfully deleted ${result.DeletedCount} tickers and ${result.CandlesDeleted} associated candles.`);
      } else {
        setSuccess(`Successfully deleted ${result.DeletedCount} tickers.`);
      }
    } catch (exc) {
      console.error('Failed to delete tickers:', exc);
      setError(exc.message || 'Failed to delete tickers');
    } finally {
      setTickerCleanupLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Data Cleanup</h2>
      </div>

      <Alert color="warning" className="mb-3" fade={false}>
        <strong>⚠️ Warning:</strong> This page allows you to permanently delete candle data. This action cannot be undone. Use with caution.
      </Alert>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      <CandleAnalyzeModal
        isOpen={analyzeModalOpen}
        toggle={() => setAnalyzeModalOpen(false)}
        onError={(msg) => setError(msg)}
      />

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Delete Candles</h5>
          <Button color="info" size="sm" onClick={() => setAnalyzeModalOpen(true)}>
            Analyze
          </Button>
        </CardHeader>
        <CardBody>
          <Form>
            <FormGroup>
              <Label for="interval">Candle Interval</Label>
              <Input
                type="select"
                id="interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                disabled={loading}
              >
                <option value="Minute">Minute</option>
                <option value="Day">Day</option>
                <option value="Week">Week</option>
                <option value="Month">Month</option>
              </Input>
              <small className="form-text text-muted">
                Select the candle interval to delete
              </small>
            </FormGroup>

            <Card className="mb-3">
              <CardHeader>
                <h6 className="mb-0">Tickers Filter</h6>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label for="tickerId">Ticker ID</Label>
                  <Input
                    type="text"
                    id="tickerId"
                    value={tickerId}
                    onChange={(e) => setTickerId(e.target.value)}
                    disabled={loading}
                    placeholder="Optional: filter by specific ticker ID"
                  />
                  <small className="form-text text-muted">
                    Optional: enter a specific ticker ID to delete candles for only that ticker.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="source">Data Source</Label>
                  <Input
                    type="select"
                    id="source"
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">All Sources</option>
                    {sources.map((src) => (
                      <option key={src} value={src}>
                        {src}
                      </option>
                    ))}
                  </Input>
                  <small className="form-text text-muted">
                    Optional: filter by data source (e.g., Polygon, MarketWatch, Norgate)
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="tickerTag">Ticker Tag</Label>
                  <Input
                    type="text"
                    id="tickerTag"
                    value={tickerTag}
                    onChange={(e) => setTickerTag(e.target.value)}
                    disabled={loading}
                    placeholder="Optional: filter by tag"
                  />
                  <small className="form-text text-muted">
                    Leave empty to apply to all tickers matching other filters
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="tickerTypes">Ticker Types</Label>
                  <Input
                    type="select"
                    id="tickerTypes"
                    multiple
                    value={selectedTickerTypes}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setSelectedTickerTypes(selected);
                    }}
                    disabled={loading}
                    style={{ height: '150px' }}
                  >
                    {tickerTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Input>
                  <small className="form-text text-muted">
                    Hold Ctrl/Cmd to select multiple types. Leave empty for all types.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="collectCandles">Collect Candles</Label>
                  <Input
                    type="select"
                    id="collectCandles"
                    value={collectCandles === null ? '' : collectCandles.toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setCollectCandles(null);
                      } else {
                        setCollectCandles(value === 'true');
                      }
                    }}
                    disabled={loading}
                  >
                    <option value="">All (ignore this filter)</option>
                    <option value="true">True (only tickers with CollectCandles enabled)</option>
                    <option value="false">False (only tickers with CollectCandles disabled)</option>
                  </Input>
                  <small className="form-text text-muted">
                    Filter by CollectCandles flag. Select 'All' to ignore this filter.
                  </small>
                </FormGroup>
              </CardBody>
            </Card>

            <Alert color="danger" className="mb-3" fade={false}>
              <strong>⚠️ Destructive Action:</strong> Clicking the button below will permanently delete all candles matching the above criteria. This cannot be undone!
            </Alert>

            <Button
              color="danger"
              onClick={handleCleanup}
              disabled={loading}
              block
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                '🗑️ Delete Candles'
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <h5 className="mb-0">Delete Candles Before Date</h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted mb-3">
            Delete candles for a specific ticker that are earlier than a given date.
          </p>
          <Form>
            <FormGroup>
              <Label for="deleteBeforeTickerId">Ticker ID</Label>
              <Input
                type="text"
                id="deleteBeforeTickerId"
                value={deleteBeforeTickerId}
                onChange={(e) => setDeleteBeforeTickerId(e.target.value)}
                disabled={deleteBeforeLoading}
                placeholder="Required: enter ticker ID"
              />
            </FormGroup>
            <FormGroup>
              <Label for="deleteBeforeInterval">Candle Interval</Label>
              <Input
                type="select"
                id="deleteBeforeInterval"
                value={deleteBeforeInterval}
                onChange={(e) => setDeleteBeforeInterval(e.target.value)}
                disabled={deleteBeforeLoading}
              >
                <option value="Minute">Minute</option>
                <option value="Day">Day</option>
                <option value="Week">Week</option>
                <option value="Month">Month</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="deleteBeforeDate">Delete candles before</Label>
              <Input
                type="date"
                id="deleteBeforeDate"
                value={deleteBeforeDate}
                onChange={(e) => setDeleteBeforeDate(e.target.value)}
                disabled={deleteBeforeLoading}
              />
              <small className="form-text text-muted">
                All candles with a date strictly earlier than this date will be deleted.
              </small>
            </FormGroup>

            <Alert color="danger" className="mb-3" fade={false}>
              <strong>⚠️ Destructive Action:</strong> Clicking the button below will permanently delete all matching candles. This cannot be undone!
            </Alert>

            <Button
              color="danger"
              onClick={handleDeleteBefore}
              disabled={deleteBeforeLoading}
              block
            >
              {deleteBeforeLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                '🗑️ Delete Candles Before Date'
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <h5 className="mb-0">Delete Tickers</h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted mb-3">
            Delete tickers from the database. This will also delete all candles and related data for these tickers.
          </p>

          <Form>
            <FormGroup>
              <Label for="tickerCleanupId">Ticker ID</Label>
              <Input
                type="text"
                id="tickerCleanupId"
                value={tickerCleanupId}
                onChange={(e) => setTickerCleanupId(e.target.value)}
                disabled={tickerCleanupLoading}
                placeholder="Optional: filter by specific ticker ID"
              />
              <small className="form-text text-muted">
                Optional: enter a specific ticker ID to delete only that ticker.
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="tickerCleanupSource">Data Source</Label>
              <Input
                type="select"
                id="tickerCleanupSource"
                value={tickerCleanupSource}
                onChange={(e) => setTickerCleanupSource(e.target.value)}
                disabled={tickerCleanupLoading}
              >
                <option value="">All Sources</option>
                {sources.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </Input>
              <small className="form-text text-muted">
                Filter by data source. Select 'All Sources' to delete tickers from all sources (use with extreme caution!).
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="tickerCleanupTypes">Ticker Type</Label>
              <Input
                type="select"
                id="tickerCleanupTypes"
                multiple
                value={tickerCleanupTypes}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setTickerCleanupTypes(selected);
                }}
                disabled={tickerCleanupLoading}
                style={{ height: '150px' }}
              >
                {tickerTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Input>
              <small className="form-text text-muted">
                Hold Ctrl/Cmd to select multiple types. Leave empty for all types.
              </small>
            </FormGroup>

            <Alert color="danger" className="mb-3" fade={false}>
              <strong>⚠️ Destructive Action:</strong> Clicking the button below will permanently delete all tickers matching the above criteria, along with all their candles and related data. This cannot be undone!
            </Alert>

            <Button
              color="danger"
              onClick={handleTickerCleanup}
              disabled={tickerCleanupLoading || loading || resetLoading}
              block
            >
              {tickerCleanupLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                '🗑️ Delete Tickers'
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>

      <Card className="mt-4">

        <CardHeader>
          <h5 className="mb-0">Reset Static Details Checked</h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted mb-3">
            Reset the StaticDetailsChecked flag for <strong>ALL tickers</strong> in the database.
            This will cause the system to re-fetch static details (industry, sector, country, etc.)
            for all tickers on the next static details pull operation.
          </p>

          <Alert color="warning" className="mb-3" fade={false}>
            <strong>⚠️ Note:</strong> This operation will mark ALL tickers to have their static details re-fetched during the next pull operation. This is useful when you want to refresh industry, sector, country, or other static information for the entire database.
          </Alert>

          <Button
            color="warning"
            onClick={handleResetStaticDetails}
            disabled={resetLoading || loading}
            block
          >
            {resetLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Resetting...
              </>
            ) : (
              '🔄 Reset Static Details Checked for All Tickers'
            )}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};
